import { useEffect, useRef } from 'react';

/**
 * Custom hook that runs a callback on mount, then on a fixed interval, but
 * pauses the interval whenever the document is hidden (background tab,
 * minimized window). When the document becomes visible again the callback
 * runs immediately and the interval restarts.
 *
 * This is used by sidebar/topbar count pollers to avoid hammering the API
 * (and the single Gunicorn worker on Cloud Run) from inactive tabs.
 *
 * @param callback - Function to invoke; latest reference is always used so
 *                   callers do not have to memoize.
 * @param intervalMs - Polling interval in milliseconds.
 * @param deps - Dependency array; when any value changes the hook restarts
 *               (matching the semantics of useEffect deps).
 */
export const useVisiblePolling = (
  callback: () => void | Promise<void>,
  intervalMs: number,
  deps: React.DependencyList = []
): void => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const run = () => {
      try {
        const result = callbackRef.current();
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch(() => {});
        }
      } catch {
        // Swallow errors so a single failure does not stop the poller.
      }
    };

    const start = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(run, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stop();
      } else {
        run();
        start();
      }
    };

    run();
    if (document.visibilityState !== 'hidden') {
      start();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
};
